import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  LinearProgress,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Typography,
} from "@mui/joy";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/joy/styles";

import { fetchAdminUsers, AdminUser } from "../../api";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const valueA = a[orderBy];
  const valueB = b[orderBy];

  if (valueA === undefined || valueA === null) {
    return 1;
  }
  if (valueB === undefined || valueB === null) {
    return -1;
  }

  if (typeof valueA === "number" && typeof valueB === "number") {
    return valueB - valueA;
  }

  const stringA = String(valueA).toLowerCase();
  const stringB = String(valueB).toLowerCase();
  if (stringB < stringA) {
    return -1;
  }
  if (stringB > stringA) {
    return 1;
  }
  return 0;
}

function getComparator(order: "asc" | "desc", orderBy: keyof AdminUser) {
  return order === "desc"
    ? (a: AdminUser, b: AdminUser) => descendingComparator(a, b, orderBy)
    : (a: AdminUser, b: AdminUser) => -descendingComparator(a, b, orderBy);
}

const USER_ROW_OPTIONS = [25, 50, 100];

export default function AdminUsers() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [originalUsers, setOriginalUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterUserType, setFilterUserType] = useState<string>("all");
  const [filterCity, setFilterCity] = useState<string>("all");
  const [filterRole, setFilterRole] = useState<string>("all");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState<keyof AdminUser>("id");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const stats = React.useMemo(() => {
    const total = users.length;
    const withEmail = users.filter((u) => u.email).length;
    const withPhone = users.filter((u) => u.phone).length;
    const withCity = users.filter((u) => u.city).length;
    const withRoles = users.filter((u) => (u.role || (u.roles && u.roles.length > 0))).length;
    return { total, withEmail, withPhone, withCity, withRoles };
  }, [users]);

  const load = async (q = "") => {
    setLoading(true);
    try {
      const data = await fetchAdminUsers(q);
      setUsers(data ?? []);
      setOriginalUsers(data ?? []);
    } catch (error) {
      console.error("Failed to load users", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSearch = () => {
    load(query);
    setPage(0);
  };

  const filterOptions = React.useMemo(() => {
    const userTypeSet = new Set<string>();
    const citySet = new Set<string>();
    const roleSet = new Set<string>();

    originalUsers.forEach((user) => {
      if (user.userType) {
        userTypeSet.add(user.userType);
      }
      if (user.city) {
        citySet.add(user.city);
      }
      if (user.role) {
        roleSet.add(user.role);
      }
      if (user.roles) {
        user.roles.forEach((role) => roleSet.add(role));
      }
    });

    const sortLabels = (values: Set<string>) => Array.from(values).sort((a, b) => a.localeCompare(b));

    return {
      userTypes: sortLabels(userTypeSet),
      cities: sortLabels(citySet),
      roles: sortLabels(roleSet),
    };
  }, [originalUsers]);

  const filteredUsers = React.useMemo(() => {
    return users.filter((user) => {
      if (filterUserType !== "all" && user.userType !== filterUserType) {
        return false;
      }
      if (filterCity !== "all" && user.city !== filterCity) {
        return false;
      }
      if (filterRole !== "all") {
        const userRoles = user.role ? [user.role] : user.roles ?? [];
        if (!userRoles.includes(filterRole)) {
          return false;
        }
      }
      return true;
    });
  }, [users, filterUserType, filterCity, filterRole]);

  const sortedUsers = React.useMemo(
    () => [...filteredUsers].sort(getComparator(order, orderBy)),
    [filteredUsers, order, orderBy],
  );

  const paginatedUsers = React.useMemo(
    () => sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedUsers, page, rowsPerPage],
  );

  const pageCount = Math.max(1, Math.ceil(filteredUsers.length / rowsPerPage));
  const statBackground = (tone: string) => {
    if (isDark) {
      if (tone === "primary") {
        return "linear-gradient(135deg, rgba(30, 64, 175, 0.25), rgba(15, 23, 42, 0.95))";
      }
      if (tone === "success") {
        return "linear-gradient(135deg, rgba(22, 163, 74, 0.22), rgba(15, 23, 42, 0.95))";
      }
      if (tone === "warning") {
        return "linear-gradient(135deg, rgba(234, 179, 8, 0.22), rgba(15, 23, 42, 0.95))";
      }
      return "linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95))";
    }
    if (tone === "primary") {
      return "linear-gradient(135deg, rgba(217, 236, 255, 0.9), rgba(241, 248, 255, 0.95))";
    }
    if (tone === "success") {
      return "linear-gradient(135deg, rgba(216, 247, 231, 0.9), rgba(240, 252, 246, 0.95))";
    }
    if (tone === "warning") {
      return "linear-gradient(135deg, rgba(255, 239, 213, 0.9), rgba(255, 248, 234, 0.95))";
    }
    return "linear-gradient(135deg, rgba(238, 241, 250, 0.9), rgba(246, 248, 252, 0.95))";
  };

  const handleRequestSort = (property: keyof AdminUser) => {
    if (orderBy === property) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrder("desc");
      setOrderBy(property);
    }
  };

  const resetFilters = () => {
    setFilterUserType("all");
    setFilterCity("all");
    setFilterRole("all");
    setQuery("");
    setPage(0);
    load("");
  };

  const renderSortIcon = (property: keyof AdminUser) => (
    <ArrowDownwardRoundedIcon
      sx={{
        fontSize: 16,
        transition: "0.2s",
        opacity: orderBy === property ? 1 : 0.4,
        transform: orderBy === property && order === "asc" ? "rotate(180deg)" : "rotate(0deg)",
      }}
    />
  );

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "xl",
        p: { xs: 2.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
        boxShadow: "sm",
      }}
    >
      <Stack spacing={0.5}>
        <Typography level="title-lg" sx={{ fontWeight: 700 }}>
          Devotee directory
        </Typography>
        <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 560 }}>
          Find devotees, seva partners, and supporters nurturing Krishna consciousness across Kanpur.
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: 1.5,
        }}
      >
        {[
          { label: "With email", value: stats.withEmail, tone: "primary" },
          { label: "With phone", value: stats.withPhone, tone: "success" },
          { label: "With city", value: stats.withCity, tone: "neutral" },
          { label: "With roles", value: stats.withRoles, tone: "warning" },
        ].map((item) => (
          <Card
            key={item.label}
            variant="soft"
            sx={{
              borderRadius: "lg",
              p: 1.5,
              background: statBackground(item.tone),
            }}
          >
            <Typography level="body-xs" sx={{ color: "text.secondary", textTransform: "uppercase", fontWeight: 700 }}>
              {item.label}
            </Typography>
            <Typography level="title-lg" sx={{ mt: 0.5 }}>
              {item.value}
            </Typography>
          </Card>
        ))}
      </Box>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} alignItems={{ lg: "flex-end" }}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Search by name, email, or phone</FormLabel>
          <Input
            size="sm"
            placeholder="Search devotees"
            value={query}
            startDecorator={<SearchRoundedIcon />}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleSearch();
              }
            }}
          />
        </FormControl>

        <FormControl sx={{ minWidth: { xs: "100%", sm: 160 } }}>
          <FormLabel>User type</FormLabel>
          <Select
            value={filterUserType}
            size="sm"
            onChange={(_, value) => {
              setFilterUserType(value ?? "all");
              setPage(0);
            }}
          >
            <Option value="all">All types</Option>
            {filterOptions.userTypes.map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: { xs: "100%", sm: 160 } }}>
          <FormLabel>City</FormLabel>
          <Select
            value={filterCity}
            size="sm"
            onChange={(_, value) => {
              setFilterCity(value ?? "all");
              setPage(0);
            }}
          >
            <Option value="all">All cities</Option>
            {filterOptions.cities.map((city) => (
              <Option key={city} value={city}>
                {city}
              </Option>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: { xs: "100%", sm: 160 } }}>
          <FormLabel>Role</FormLabel>
          <Select
            value={filterRole}
            size="sm"
            onChange={(_, value) => {
              setFilterRole(value ?? "all");
              setPage(0);
            }}
          >
            <Option value="all">All roles</Option>
            {filterOptions.roles.map((role) => (
              <Option key={role} value={role}>
                {role}
              </Option>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="plain"
          color="neutral"
          startDecorator={<FilterAltOffRoundedIcon />}
          onClick={resetFilters}
        >
          Clear filters
        </Button>
      </Stack>

      <Sheet
        variant="outlined"
        sx={{
          borderRadius: "lg",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {loading && <LinearProgress sx={{ position: "absolute", insetInline: 0, top: 0, zIndex: 1 }} />}
        <Table
          stickyHeader
          borderAxis="bothBetween"
          hoverRow
          size="sm"
          sx={{
            minWidth: 1200,
            "--TableCell-headBackground": "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "14px",
            "--TableCell-paddingX": "16px",
            "& thead": {
              "& tr": {
                backgroundColor: "var(--joy-palette-background-level2)",
              }
            },
            "& th": {
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "text.secondary",
              fontWeight: 700,
              borderBottom: "1px solid",
              borderColor: "divider",
            },
            "& td": {
              fontSize: "0.875rem",
              borderBottom: "1px solid",
              borderColor: "divider",
            },
            "& tbody tr:hover": {
              backgroundColor: "var(--joy-palette-background-level1)",
            },
            "& th:nth-of-type(1), & td:nth-of-type(1)": {
              width: "60px",
            },
            "& th:nth-of-type(2), & td:nth-of-type(2)": {
              minWidth: "260px",
            },
            "& th:nth-of-type(3), & td:nth-of-type(3)": {
              width: "90px",
            },
            "& th:nth-of-type(4), & td:nth-of-type(4)": {
              minWidth: "200px",
            },
            "& th:nth-of-type(5), & td:nth-of-type(5)": {
              minWidth: "160px",
            },
            "& th:nth-of-type(6), & td:nth-of-type(6)": {
              minWidth: "140px",
            },
            "& th:nth-of-type(7), & td:nth-of-type(7)": {
              width: "100px",
            },
            "& th:nth-of-type(8), & td:nth-of-type(8)": {
              width: "110px",
            },
          }}
        >
          <thead>
            <tr>
              <th scope="col">
                <Button
                  size="sm"
                  variant="plain"
                  color={orderBy === "id" ? "primary" : "neutral"}
                  endDecorator={renderSortIcon("id")}
                  onClick={() => handleRequestSort("id")}
                  sx={{ px: 0 }}
                >
                  ID
                </Button>
              </th>
              <th scope="col">Devotee</th>
              <th scope="col">
                <Button
                  size="sm"
                  variant="plain"
                  color={orderBy === "userType" ? "primary" : "neutral"}
                  endDecorator={renderSortIcon("userType")}
                  onClick={() => handleRequestSort("userType")}
                  sx={{ px: 0 }}
                >
                  Type
                </Button>
              </th>
              <th scope="col">Contact</th>
              <th scope="col">Location</th>
              <th scope="col">Role</th>
              <th scope="col">
                <Button
                  size="sm"
                  variant="plain"
                  color={orderBy === "createdAt" ? "primary" : "neutral"}
                  endDecorator={renderSortIcon("createdAt")}
                  onClick={() => handleRequestSort("createdAt")}
                  sx={{ px: 0 }}
                >
                  Joined
                </Button>
              </th>
              <th scope="col" style={{ width: 130 }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.length === 0 && !loading ? (
              <tr>
                <td colSpan={8}>
                  <Stack spacing={1} sx={{ py: 4, alignItems: "center" }}>
                    <PeopleRoundedIcon color="primary" />
                    <Typography level="title-sm">No devotees match the current filters.</Typography>
                    <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 420, textAlign: "center" }}>
                      Try widening your filters or clearing the search to rediscover our seva family.
                    </Typography>
                  </Stack>
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                      {user.id}
                    </Typography>
                  </td>
                  <td>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                      <Avatar size="sm" sx={{ flexShrink: 0 }}>
                        {user.displayName
                          ? user.displayName
                              .split(" ")
                              .filter(Boolean)
                              .slice(0, 2)
                              .map((part) => part[0]!.toUpperCase())
                              .join("")
                          : "?"}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography level="body-sm" sx={{ fontWeight: 600, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                          {user.displayName || "Anonymous"}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: "text.tertiary", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                          {user.firebaseUid ? user.firebaseUid.slice(0, 12) : "–"}
                        </Typography>
                      </Box>
                    </Stack>
                  </td>
                  <td>
                    <Chip size="sm" variant="soft" color="neutral">
                      {user.userType || "–"}
                    </Chip>
                  </td>
                  <td>
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                      <Typography level="body-sm" sx={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                        {user.email || "–"}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: "text.tertiary", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                        {user.phone || "–"}
                      </Typography>
                    </Stack>
                  </td>
                  <td>
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                      <Typography level="body-sm" sx={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                        {user.city || "–"}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: "text.tertiary", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                        {user.country || "–"}
                      </Typography>
                    </Stack>
                  </td>
                  <td>
                    {user.role || (user.roles && user.roles.length > 0) ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                        {(user.role ? [user.role] : user.roles ?? []).slice(0, 1).map((role) => (
                          <Chip key={role} size="sm" variant="soft" sx={{ maxWidth: "100%", textOverflow: "ellipsis", overflow: "hidden" }}>
                            {role}
                          </Chip>
                        ))}
                        {(user.role ? [user.role] : user.roles ?? []).length > 1 && (
                          <Chip size="sm" variant="soft" color="neutral">
                            +{(user.role ? [user.role] : user.roles ?? []).length - 1}
                          </Chip>
                        )}
                      </Stack>
                    ) : (
                      "–"
                    )}
                  </td>
                  <td>
                    <Typography level="body-sm">
                      {new Date(user.createdAt).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </Typography>
                  </td>
                  <td>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        size="sm"
                        variant="soft"
                        startDecorator={<VisibilityRoundedIcon fontSize="small" />}
                        component={Link}
                        to={`/users/${user.id}`}
                      >
                        View
                      </Button>
                    </Stack>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Sheet>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center" justifyContent="space-between">
        <Typography level="body-sm" sx={{ color: "text.secondary" }}>
          Showing {paginatedUsers.length === 0 ? 0 : page * rowsPerPage + 1}–
          {Math.min(filteredUsers.length, (page + 1) * rowsPerPage)} of {filteredUsers.length} devotees
        </Typography>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <FormControl orientation="horizontal" size="sm">
            <FormLabel>Rows per page</FormLabel>
            <Select
              value={rowsPerPage}
              onChange={(_, value) => {
                if (!value) return;
                setRowsPerPage(value);
                setPage(0);
              }}
            >
              {USER_ROW_OPTIONS.map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="sm"
              variant="outlined"
              color="neutral"
              disabled={page === 0}
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
            >
              <KeyboardArrowLeftRoundedIcon />
            </IconButton>
            <IconButton
              size="sm"
              variant="outlined"
              color="neutral"
              disabled={page >= pageCount - 1}
              onClick={() => setPage((prev) => Math.min(prev + 1, pageCount - 1))}
            >
              <KeyboardArrowRightRoundedIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
}
